import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { candidateAPI } from '../utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// ResumeButton — open a candidate's CV in a new tab, from anywhere.
//
// Always goes through GET /candidates/:id/resume-url, which returns a short-lived
// S3 pre-signed URL. That path is:
//   • authenticated  — the request carries the JWT like any other XHR
//   • stateless      — the signed URL is validated by S3, not by our process
//
// The alternative in the codebase, <a href=".../candidates/:id/resume">, cannot
// send an Authorization header and used to depend on server-held state, so it
// broke roughly half the time behind two pods. This component exists so every
// surface uses the one mechanism that is correct.
//
// The URL is fetched on CLICK, never on render: pre-signed URLs expire, so one
// obtained when a card first painted may well be dead by the time it is used.
// ─────────────────────────────────────────────────────────────────────────────

export default function ResumeButton({
  candidateId,
  resumeName,
  variant = 'button',     // 'button' | 'link'
  label,
  style = {},
  className = '',
}) {
  const [loading, setLoading] = useState(false);

  const open = async (e) => {
    // Cards and rows are themselves clickable — never let this bubble into them.
    e?.preventDefault();
    e?.stopPropagation();
    if (loading) return;

    setLoading(true);
    try {
      const { data } = await candidateAPI.getResumeUrl(candidateId);
      if (!data?.url) {
        toast.error('No resume on file for this candidate.');
        return;
      }
      // noopener: the new tab must not get a handle on this window.
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // 404 is the ordinary "nothing uploaded" case and deserves plain words,
      // not a stack trace or a silent no-op.
      const status = err?.response?.status;
      toast.error(status === 404
        ? 'No resume on file for this candidate.'
        : 'Could not open the resume. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const text = label || 'View Resume';

  if (variant === 'link') {
    return (
      <button
        type="button"
        onClick={open}
        disabled={loading}
        title={resumeName || text}
        className={className}
        style={{
          background: 'none', border: 'none', padding: 0,
          color: '#6366f1', fontWeight: 600, fontSize: 12,
          cursor: loading ? 'wait' : 'pointer', textDecoration: 'underline',
          opacity: loading ? 0.6 : 1, ...style,
        }}
      >
        {loading ? 'Opening…' : text}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      title={resumeName || text}
      className={className}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
        border: '1px solid var(--border-muted)', background: 'var(--surface-2, transparent)',
        color: 'var(--text-primary)', cursor: loading ? 'wait' : 'pointer',
        opacity: loading ? 0.6 : 1, ...style,
      }}
    >
      📄 {loading ? 'Opening…' : text}
    </button>
  );
}
